import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { DataService } from 'api/DataService'
import copy from 'copy-to-clipboard'
import { useFetch } from 'hooks/use-fetch'
import {
  amountTypeEnum,
  issueWhenEnum,
  natureOperationEnum
} from 'models/Invoice'
import { NFeParams, NfseScheduled } from 'models/NFe'
import moment from 'moment'
import {
  Status as ChargeStatus,
  getStatusOptionCharge
} from 'templates/v2/Charge/components/ChargeTable'

import Can from 'components/Can'
import { DetailCompanyRow } from 'components/DetailCompany'
import IconContainer from 'components/IconContainer/IconContainer'

import { formatMoney } from 'utils'
import { toastProps } from 'utils/types/toast-props'

import ModalChargeDetail from '../ModalChargeDetail'
import ModalInvoiceContent from '../ModalInvoice/ModalInvoiceContent'
import { getHoverText } from './ModalInvoiceDetails.utils'
import * as S from './ModalInvoiceDetailsContent.styles'
import { getStyle } from './ModalInvoiceDetailsContent.styles'
import ModalInvoiceSkeleton from './ModalInvoiceSkeleton'

interface ModalInvoiceDetailsContentProps {
  uuid: string
  nfseScheduledUuid?: string
  handleReloadTable: () => void
  handleCloseModal: () => void
  setFullHeight?: Dispatch<SetStateAction<boolean>>
}

const ModalInvoiceDetailsContent = ({
  uuid,
  nfseScheduledUuid,
  handleReloadTable,
  handleCloseModal,
  setFullHeight
}: ModalInvoiceDetailsContentProps) => {
  const isScheduledMode = !!nfseScheduledUuid

  const [loading, setLoading] = useState(false)
  const [invoice, setInvoice] = useState<NFeParams>({} as NFeParams)
  const [scheduled, setScheduled] = useState<NfseScheduled>({} as NfseScheduled)
  const [confirmCancelScheduled, setConfirmCancelScheduled] = useState(false)
  const [confirmEmitNow, setConfirmEmitNow] = useState(false)

  const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false)
  const [selectedBillingUuid, setSelectedBillingUuid] = useState<string | null>(
    null
  )

  const isMobile = useMediaQuery('(max-width:600px)')

  const [openDrawer, setOpenDrawer] = useState(false)
  const [confirmSend, setConfirmSend] = useState(false)
  const [isOpenDialogReissue, setIsOpenDialogReissue] = useState(false)
  const handleToggleDrawer = () => {
    setOpenDrawer((open) => !open)
  }

  const copyUuid = () => {
    if (!uuid) return
    copy(uuid)
    toast.success('Identificador copiado para a área de transferência')
  }

  const handletoggleConfirmSend = (): void => {
    setConfirmSend((prevState) => !prevState)
  }

  // Sem o guard, abrir o modal em modo agendamento (uuid ausente) dispara
  // GET /v1/nfses/null e a nota nunca é encontrada. Só busca quando o
  // identificador do modo ativo existe.
  const detailsUuid = isScheduledMode ? nfseScheduledUuid : uuid

  const [, result, , status] = useFetch({
    shouldRun: !!detailsUuid,
    method: 'GET',
    url: isScheduledMode
      ? `/v1/nfse_scheduleds/${nfseScheduledUuid}`
      : `/v1/nfses/${uuid}`
  })

  const cancelScheduled = async () => {
    const id = toast.loading('Cancelando agendamento de emissão da NF...')
    const response = await DataService({
      type: 'DELETE',
      url: `/v1/nfse_scheduleds/${nfseScheduledUuid}`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    setConfirmCancelScheduled(false)

    if (response.status === 200) {
      handleReloadTable()
      handleCloseModal()
    }
  }

  // "Emitir agora" adianta a emissão sem esperar a condição do agendamento.
  // Fica desabilitado apenas quando o valor da nota é o valor pago e a fatura
  // ainda não foi paga — sairia nota com valor divergente.
  const disableEmitNow =
    scheduled?.amount_type === 'amount_paid' && !scheduled?.billing_paid

  const emitNow = async () => {
    const id = toast.loading('Emitindo nota fiscal...')
    const response = await DataService({
      type: 'POST',
      url: `/v1/nfse_scheduleds/${nfseScheduledUuid}/emit_now`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    setConfirmEmitNow(false)

    if (response.status === 200) {
      handleReloadTable()
      handleCloseModal()
    }
  }

  const deleteNFS = async () => {
    if (!uuid) return
    const id = toast.loading('Cancelando nota fiscal...')
    const response = await DataService({
      type: 'DELETE',
      url: `/v1/nfses/${uuid}`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    if (response.status === 200) {
      handleReloadTable()
      handleCloseModal()
    }
    handleToggleDrawer()
  }

  const reissue = async () => {
    if (!uuid) return
    setLoading(true)
    const id = toast.loading('Reemitindo nota fiscal...')
    const response = await DataService({
      type: 'PATCH',
      url: `v1/nfses/${uuid}/reissue`
    })
    setLoading(false)
    setIsOpenDialogReissue(false)
    if (response.status === 200) {
      handleReloadTable()
      handleCloseModal()
    }

    toast.update(id, {
      render: response.error ? response.message : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })
  }

  const sendNF = async () => {
    if (!uuid) return
    setLoading(true)
    const id = toast.loading('Enviando nota fiscal...')
    const response = await DataService({
      type: 'GET',
      url: `/v1/nfses/${uuid}/notify`
    })
    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    setLoading(false)

    handletoggleConfirmSend()
  }

  useEffect(() => {
    if (isScheduledMode) {
      setScheduled(result?.data as NfseScheduled)
    } else {
      setInvoice(result?.data as NFeParams)
    }
  }, [result])

  if (status !== 'settled') {
    return <ModalInvoiceSkeleton />
  }

  if (isScheduledMode) {
    return (
      <ModalBody>
        <ModalHeader onClose={handleCloseModal}>
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            mr={2}
            width="100%"
          >
            <IconContainer icon="info" />
            <Typography variant="mdBold">
              Detalhes do agendamento de NF
            </Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          <S.Wrapper>
            <S.WrapperContent>
              <Box
                display="flex"
                flexDirection="column"
                width="100%"
                gap="12px"
              >
                {/* SC-10999: de qual empresa é a nota, na visão multi-empresas */}
                <DetailCompanyRow recordUuid={uuid} />
                {scheduled?.nfse_issuer?.legal_name && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="neutral.400">
                      Prestador
                    </Typography>
                    <S.Separator />
                    <Typography variant="smBold">
                      {scheduled?.nfse_issuer?.legal_name}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography variant="smLight" color="neutral.400">
                    ISS retido
                  </Typography>
                  <S.Separator />
                  <Typography variant="smBold">
                    {scheduled?.iss_retention ? 'Sim' : 'Não'}
                  </Typography>
                </Box>

                {scheduled?.nfse_policy && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="neutral.400">
                      Emissão
                    </Typography>
                    <S.Separator />
                    <Typography variant="smBold">
                      {issueWhenEnum[
                        scheduled?.nfse_policy as keyof typeof issueWhenEnum
                      ] || scheduled?.nfse_policy}
                    </Typography>
                  </Box>
                )}

                {scheduled?.issue_date && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="neutral.400">
                      Data programada
                    </Typography>
                    <S.Separator />
                    <Typography variant="smBold">
                      {moment(scheduled?.issue_date).format('DD/MM/YYYY')}
                    </Typography>
                  </Box>
                )}

                {((scheduled as any)?.day_of_month ||
                  scheduled?.specific_day) && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="neutral.400">
                      Dia do mês
                    </Typography>
                    <S.Separator />
                    <Typography variant="smBold">
                      {(scheduled as any)?.day_of_month ||
                        scheduled?.specific_day}
                    </Typography>
                  </Box>
                )}

                {scheduled?.nfse_issuer_service && (
                  <Box display="flex" flexDirection="column">
                    <Typography variant="smLight" color="neutral.400" mb="6px">
                      Item lista de serviço
                    </Typography>
                    <Typography variant="smBold">
                      {scheduled?.nfse_issuer_service?.code} -{' '}
                      {scheduled?.nfse_issuer_service?.description}
                    </Typography>
                  </Box>
                )}

                {(scheduled?.amount_type || scheduled?.amount) && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="neutral.400">
                      Valor da nota
                    </Typography>
                    <S.Separator />
                    <Typography variant="smBold">
                      {(amountTypeEnum as Record<string, string>)[
                        scheduled?.amount_type
                      ] || ''}{' '}
                      {scheduled?.amount
                        ? `- ${formatMoney(Number(scheduled?.amount))}`
                        : ''}
                    </Typography>
                  </Box>
                )}

                {scheduled?.nature_operation && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="neutral.400">
                      Natureza da operação
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography variant="smBold">
                      {(natureOperationEnum as Record<string, string>)[
                        scheduled?.nature_operation
                      ] || scheduled?.nature_operation}
                    </Typography>
                  </Box>
                )}

                {scheduled?.description && (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="smLight" color="neutral.400">
                      Descrição
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-line' }}>
                      {scheduled?.description}
                    </Typography>
                  </Box>
                )}
              </Box>
            </S.WrapperContent>
          </S.Wrapper>
        </ModalContent>

        <ModalFooter>
          {/* Emitir agora (emit_now) exige nfses:manage; cancelar o agendamento
              (destroy) e cancelar a nota exigem nfses:cancel. */}
          {scheduled?.nfseable_type === 'Billing' && (
            <Can permission="nfses:manage">
              <Tooltip
                title={
                  disableEmitNow
                    ? 'A fatura ainda não foi paga. Como o valor da nota é o valor pago, não é possível emitir agora.'
                    : ''
                }
              >
                <span>
                  <Button
                    variantButton="outlined"
                    onClick={() => setConfirmEmitNow(true)}
                    disabled={disableEmitNow}
                  >
                    Emitir agora
                  </Button>
                </span>
              </Tooltip>
            </Can>
          )}
          <Can permission="nfses:cancel">
            <Button
              variantButton="outlined"
              onClick={() => setConfirmCancelScheduled(true)}
            >
              Cancelar agendamento de emissão da NF
            </Button>
          </Can>
        </ModalFooter>

        <Dialog
          icon="info"
          title="Cancelar agendamento de emissão da NF?"
          description="A nota fiscal programada para esta cobrança não será emitida. Essa ação não pode ser desfeita."
          setIsOpenDialog={setConfirmCancelScheduled}
          isOpenDialog={confirmCancelScheduled}
          cancelButton
          cancelButtonLabel="Voltar"
          PaperProps={{ sx: { maxWidth: '440px', width: '100%' } }}
        >
          <Button onClick={cancelScheduled} fullWidth>
            Cancelar agendamento
          </Button>
        </Dialog>

        <Dialog
          icon="info"
          title="Emitir a nota fiscal agora?"
          description="A nota fiscal será emitida imediatamente, sem esperar a condição do agendamento. Deseja prosseguir?"
          setIsOpenDialog={setConfirmEmitNow}
          isOpenDialog={confirmEmitNow}
          cancelButton
          cancelButtonLabel="Voltar"
          PaperProps={{ sx: { maxWidth: '440px', width: '100%' } }}
        >
          <Button onClick={emitNow} fullWidth>
            Emitir agora
          </Button>
        </Dialog>
      </ModalBody>
    )
  }

  return (
    <ModalBody>
      <ModalHeader onClose={handleCloseModal}>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'start' : 'center'}
          mr={2}
          width="100%"
          gap={isMobile ? 1 : 0}
        >
          <Tooltip
            title={uuid ? `Identificador: ${uuid}` : ''}
            placement="top"
            onClick={copyUuid}
            arrow
            PopperProps={{
              onClick: copyUuid,
              sx: { cursor: 'pointer' }
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              sx={{ cursor: uuid ? 'pointer' : 'default' }}
            >
              <IconContainer icon="info" />
              <Typography variant="mdBold">Detalhes da nota fiscal</Typography>
            </Stack>
          </Tooltip>
          <Tooltip
            arrow
            title={getHoverText(invoice?.status || 'pending').text}
          >
            <S.Status status={invoice?.status || 'pending'}>
              {getStyle(invoice?.status || 'pending')?.label}
            </S.Status>
          </Tooltip>
        </Stack>
      </ModalHeader>

      <ModalContent>
        <S.Wrapper>
          <S.WrapperContent>
            <Box display="flex" flexDirection="column" width="100%" gap="12px">
              {/* SC-10999: de qual empresa é a nota, na visão multi-empresas */}
              <DetailCompanyRow recordUuid={uuid} />
              {invoice?.nfse_number && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography variant="smLight" color="neutral.400">
                    Número da nota
                  </Typography>
                  <S.Separator />
                  <Typography variant="smBold">
                    {invoice?.nfse_number}
                  </Typography>
                </Box>
              )}

              {invoice?.nfse_issuer?.legal_name && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography variant="smLight" color="neutral.400">
                    Prestador
                  </Typography>
                  <S.Separator />
                  <Typography variant="smBold">
                    {invoice?.nfse_issuer?.legal_name}
                  </Typography>
                </Box>
              )}

              {invoice?.nfse_item?.created_at && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography variant="smLight" color="neutral.400">
                    Data/Hora emissão
                  </Typography>
                  <S.Separator />
                  <Typography variant="smBold">
                    {moment(invoice?.nfse_item?.created_at).format(
                      'DD/MM/YYYY HH:mm:ss'
                    )}
                  </Typography>
                </Box>
              )}
              {invoice?.customer?.name && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography variant="smLight" color="neutral.400">
                    Cliente
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography
                    variant="smBold"
                    display="flex"
                    alignItems="center"
                  >
                    {invoice?.customer?.name}{' '}
                    <Icon
                      icon="open"
                      width="21px"
                      height="21px"
                      ml="6px"
                      onClick={() =>
                        window.open(
                          `/clientes/${invoice?.customer?.uuid}`,
                          '_blank'
                        )
                      }
                      sx={{ cursor: 'pointer', color: 'neutral.400' }}
                    />
                  </Typography>
                </Box>
              )}

              {invoice?.nfse_item?.amount && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography variant="smLight" color="neutral.400">
                    Valor
                  </Typography>
                  <S.Separator />
                  <Typography variant="smBold">
                    {formatMoney(invoice?.nfse_item.amount)}
                  </Typography>
                </Box>
              )}

              {invoice?.nfse_item?.nfse_issuer_service && (
                <Box display="flex" flexDirection="column">
                  <Typography variant="smLight" color="neutral.400" mb="6px">
                    Item lista de serviço
                  </Typography>
                  <Typography variant="smBold">
                    {invoice?.nfse_item?.nfse_issuer_service.code} -{' '}
                    {invoice?.nfse_item?.nfse_issuer_service.description}
                  </Typography>
                </Box>
              )}

              {invoice?.nfse_item?.description && (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography variant="smLight" color="neutral.400">
                    Descrição
                  </Typography>
                  <Typography sx={{ whiteSpace: 'pre-line' }}>
                    {invoice?.nfse_item?.description}
                  </Typography>
                </Box>
              )}

              {invoice?.status_detail && (
                <Box display="flex" flexDirection="column">
                  <Typography variant="smLight" color="error.200" mb="6px">
                    Erro
                  </Typography>
                  <Typography variant="smBold" color="error.200">
                    {invoice?.status_detail}
                  </Typography>
                </Box>
              )}
            </Box>
          </S.WrapperContent>

          {invoice?.billings && invoice.billings.length > 0 && (
            <Box display="flex" flexDirection="column" gap="12px">
              <Typography variant="smBold" color="neutral.200">
                Cobrança{invoice.billings.length > 1 ? 's' : ''} relacionada
                {invoice.billings.length > 1 ? 's' : ''}
              </Typography>
              <S.BillingsSection>
                {invoice.billings.map((billing) => (
                  <S.BillingCard
                    key={billing.uuid}
                    onClick={() => setSelectedBillingUuid(billing.uuid)}
                    title="Clique para ver detalhes da cobrança"
                  >
                    <S.BillingHeader>
                      <Typography
                        variant="smBold"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}
                      >
                        {billing.description || 'Sem descrição'}
                      </Typography>
                      <ChargeStatus
                        status={billing.status}
                        dueDate={billing.due_date}
                        sx={{ fontSize: '12px', padding: '2px 6px' }}
                      >
                        {
                          getStatusOptionCharge(
                            billing.status,
                            billing.due_date
                          ).label
                        }
                      </ChargeStatus>
                    </S.BillingHeader>
                    <S.BillingInfo>
                      <Box display="flex" gap={0.5} alignItems="center">
                        <Typography variant="smLight" color="neutral.400">
                          Vencimento:
                        </Typography>
                        <Typography variant="smBold">
                          {billing.due_date
                            ? moment(billing.due_date).format('DD/MM/YYYY')
                            : '-'}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={0.5} alignItems="center">
                        <Typography variant="smLight" color="neutral.400">
                          Valor:
                        </Typography>
                        <Typography variant="smBold">
                          {formatMoney(billing.amount_billed)}
                        </Typography>
                      </Box>
                      {billing.amount_paid && billing.amount_paid !== '0.0' && (
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Typography variant="smLight" color="neutral.400">
                            Pago:
                          </Typography>
                          <Typography variant="smBold">
                            {formatMoney(billing.amount_paid)}
                          </Typography>
                        </Box>
                      )}
                      {billing.date_paid && (
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Typography variant="smLight" color="neutral.400">
                            Pagamento:
                          </Typography>
                          <Typography variant="smBold">
                            {moment(billing.date_paid).format('DD/MM/YYYY')}
                          </Typography>
                        </Box>
                      )}
                    </S.BillingInfo>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                      <Icon
                        icon="open"
                        width="16px"
                        height="16px"
                        sx={{ color: 'neutral.400' }}
                      />
                      <Typography variant="smLight" color="neutral.400">
                        Ver detalhes da cobrança
                      </Typography>
                    </Box>
                  </S.BillingCard>
                ))}
              </S.BillingsSection>
            </Box>
          )}
        </S.Wrapper>
      </ModalContent>

      <ModalFooter>
        {/* O link envolve o botão inteiro: com o <a> dentro do botão, o toque
            na borda (comum no celular) não baixava nada. */}
        {invoice?.url_pdf && (
          <a
            href={invoice?.url_pdf}
            download
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Button variantButton="outlined">
              <Typography variant="smBold" color="neutral.100">
                Baixar PDF
              </Typography>
            </Button>
          </a>
        )}

        {invoice?.url_xml && (
          <a
            href={invoice?.url_xml}
            download
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Button variantButton="outlined">
              <Typography variant="smBold" color="neutral.100">
                Baixar XML
              </Typography>
            </Button>
          </a>
        )}

        {invoice?.status === 'confirmed' && (
          <Can permission="nfses:cancel">
            <Button variantButton="outlined" onClick={handleToggleDrawer}>
              Cancelar NF
            </Button>
          </Can>
        )}
        {invoice?.status === 'failed' && (
          <Can permission="nfses:manage">
            <Button
              onClick={() => {
                !!setFullHeight && setFullHeight(true)
                setIsOpenEditModal(true)
              }}
            >
              Reemitir NF
            </Button>
          </Can>
        )}

        {/* TODO: A implementar ainda */}
        {invoice?.status == 'confirmed' && (
          <Can permission="nfses:manage">
            <Button
              variantButton="primary"
              onClick={handletoggleConfirmSend}
              disabled={loading}
              loading={loading}
            >
              Enviar nota para o cliente
            </Button>
          </Can>
        )}
      </ModalFooter>

      <Dialog
        icon="info"
        title="Cancelar nota fiscal"
        description="Confirma o cancelamento desta nota fiscal?"
        setIsOpenDialog={handleToggleDrawer}
        isOpenDialog={openDrawer}
        cancelButton
        cancelButtonLabel="Voltar"
      >
        <Button onClick={deleteNFS} fullWidth>
          Cancelar
        </Button>
      </Dialog>

      <Dialog
        icon="info"
        title="Reemitir nota fiscal"
        description="Confirma a reemissão desta nota fiscal?"
        setIsOpenDialog={setIsOpenDialogReissue}
        isOpenDialog={isOpenDialogReissue}
        cancelButton
      >
        <Button onClick={reissue} fullWidth>
          Reemitir
        </Button>
      </Dialog>

      <Dialog
        icon="info"
        title="Enviar nota fiscal"
        description="Confirma o envio dessa nota fiscal por e-mail?"
        setIsOpenDialog={handletoggleConfirmSend}
        isOpenDialog={confirmSend}
        cancelButton
      >
        <Button onClick={sendNF} fullWidth>
          Enviar
        </Button>
      </Dialog>

      <Modal
        open={isOpenEditModal}
        handleClose={() => {
          setIsOpenEditModal(false)
          !!setFullHeight && setFullHeight(false)
          handleCloseModal()
        }}
        width="100%"
        maxWidth="85vw"
        maxHeight="95vh"
        height="100%"
      >
        <ModalInvoiceContent
          handleToggle={() => {
            setIsOpenEditModal(false)
            !!setFullHeight && setFullHeight(false)
            handleCloseModal()
          }}
          isEdit
          invoice={invoice}
          handleReloadTable={handleReloadTable}
        />
      </Modal>

      {selectedBillingUuid && (
        <ModalChargeDetail
          uuid={selectedBillingUuid}
          isOpenChargeDetail={!!selectedBillingUuid}
          handleCloseChargeDetail={() => setSelectedBillingUuid(null)}
        />
      )}
    </ModalBody>
  )
}

export default ModalInvoiceDetailsContent
